import { useState } from 'react';
import { GIFT_FULFILMENT_LABELS, GIFT_REQUEST_STATUS_LABELS } from '@affily/shared/constants';
import { formatCents } from '@affily/shared/money';
import { api } from '../lib/api.js';
import { Button, Badge, Alert } from './ui.jsx';
import { GiftDetailsForm, emptyDetails, detailsPayload, isOutOfStock } from './GiftPicker.jsx';

export const giftStatusTone = {
  requested: 'caution',
  approved: 'accent',
  sent: 'accent',
  redeemed: 'positive',
  declined: 'neutral',
  cancelled: 'neutral',
};

const nextStep = {
  requested: 'Waiting for the brand to approve.',
  approved: 'Approved. The brand will mark it sent.',
  sent: 'On its way. Mark it received once you have it.',
};

/**
 * The gifts a creator may ask for on one partnership, and where each request
 * stands. Eligibility is the FROZEN terms (what was on offer when they were
 * accepted); whether the brand still offers it, and has any left, comes from
 * the live program.
 */
export function PartnershipGifts({ partnership, requests, onChanged }) {
  const [openKey, setOpenKey] = useState(null);
  const [details, setDetails] = useState(emptyDetails());
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  const frozen = partnership.terms.gifts ?? [];
  if (!frozen.length) return null;
  const live = partnership.program?.gifts ?? [];
  const canAsk = partnership.status === 'active';

  const forGift = (key) =>
    requests
      .filter((r) => r.programId === partnership.program?.id && r.giftKey === key)
      .sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt));

  async function run(id, action, body) {
    setBusy(id);
    setError('');
    try {
      await api.post(`/gift-requests/${id}/${action}`, body ?? {});
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  }

  async function submit(gift) {
    setBusy(gift.key);
    setError('');
    setFieldErrors({});
    try {
      await api.post(`/partnerships/${partnership.id}/gift-requests`, {
        giftKey: gift.key,
        ...detailsPayload(gift.fulfilment, details),
      });
      setOpenKey(null);
      setDetails(emptyDetails());
      onChanged();
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="border-t border-rule p-4">
      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">Gifts</p>
      <p className="mt-0.5 text-xs text-ink-muted">
        Free, and separate from your commission. Values are the brand’s own figures.
      </p>
      <Alert className="mt-2">{error}</Alert>

      <ul className="mt-3 space-y-3">
        {frozen.map((gift) => {
          const liveGift = live.find((g) => g.key === gift.key);
          const [latest] = forGift(gift.key);
          const open = latest?.isOpen ? latest : null;
          const soldOut = liveGift && isOutOfStock(liveGift);
          const askable = canAsk && !open && liveGift && !soldOut;

          return (
            <li key={gift.key} className="rounded-md border border-rule bg-paper-sunken p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{gift.name}</p>
                  <p className="text-xs text-ink-muted">
                    {GIFT_FULFILMENT_LABELS[gift.fulfilment]}
                    {gift.retailValueCents != null &&
                      ` · ${formatCents(gift.retailValueCents, { dropZeroCents: true, currency: partnership.terms.currency })} brand-stated value`}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {latest && (
                    <Badge tone={giftStatusTone[latest.status]}>
                      {GIFT_REQUEST_STATUS_LABELS[latest.status]}
                    </Badge>
                  )}
                  {open && ['requested', 'approved'].includes(open.status) && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy === open.id}
                      onClick={() => run(open.id, 'cancel')}
                    >
                      Cancel request
                    </Button>
                  )}
                  {open?.status === 'sent' && (
                    <Button size="sm" disabled={busy === open.id} onClick={() => run(open.id, 'redeemed')}>
                      Mark received
                    </Button>
                  )}
                  {askable && openKey !== gift.key && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setOpenKey(gift.key);
                        setDetails(emptyDetails());
                        setFieldErrors({});
                        setError('');
                      }}
                    >
                      {latest ? 'Request again' : 'Request'}
                    </Button>
                  )}
                </div>
              </div>

              {open && <p className="mt-2 text-xs text-ink-muted">{nextStep[open.status]}</p>}
              {latest?.decisionNote && (
                <p className="mt-1 text-xs text-ink-muted">Brand’s note: {latest.decisionNote}</p>
              )}
              {latest?.sentNote && (
                <p className="mt-1 text-xs text-ink-muted">Tracking / reference: {latest.sentNote}</p>
              )}
              {open?.details && (
                <p className="mt-1 text-xs text-ink-faint">
                  Sent to the brand:{' '}
                  {open.details.shippingAddress
                    ? [
                        open.details.shippingAddress.line1,
                        open.details.shippingAddress.city,
                        open.details.shippingAddress.postalCode,
                        open.details.shippingAddress.country,
                      ]
                        .filter(Boolean)
                        .join(', ')
                    : open.details.bookingNote}
                </p>
              )}
              {!liveGift && !open && <p className="mt-2 text-xs text-ink-faint">The brand no longer offers this gift.</p>}
              {soldOut && !open && <p className="mt-2 text-xs text-ink-faint">Out of stock.</p>}

              {openKey === gift.key && (
                <form
                  className="mt-3 space-y-3 border-t border-rule pt-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    submit(gift);
                  }}
                >
                  <GiftDetailsForm
                    fulfilment={gift.fulfilment}
                    value={details}
                    onChange={setDetails}
                    errors={fieldErrors}
                  />
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={busy === gift.key}>
                      {busy === gift.key ? 'Sending…' : 'Send request'}
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setOpenKey(null)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
